import React from "react";
// findDOMNode removed in React 19, using ref instead
import screenfull from "screenfull";
import Mark from "../../../common/components/mark/Mark";

// PDF.js v3.x with React 19 compatibility - Direct rendering without react-pdf
let pdflib = null;
let pdfSupported = true;

try {
  pdflib = require("pdfjs-dist");

  // Configure worker for PDF.js v3.x
  if (pdflib.GlobalWorkerOptions) {
    const isOfflineContext =
      typeof window !== "undefined" &&
      (window.location.protocol === "file:" || (typeof State !== "undefined" && State));

    if (isOfflineContext) {
      pdflib.GlobalWorkerOptions.workerSrc = "images/pdf.worker.min.js";
    } else {
      pdflib.GlobalWorkerOptions.workerSrc =
        "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";
    }
  }

  console.log("EnrichedPDF plugin loaded successfully");
} catch (error) {
  console.error("PDF.js could not be loaded:", error);
  pdfSupported = false;
}
/* eslint-disable react/prop-types */

export default class EnrichedPDFPlugin extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      fullscreen: false,
      numPages: null,
      scale: 1,
      pageNumber: 1,
      rotate: 0,
      pdfDocument: null,
      loading: true,
      error: null,
    };
    this.canvasRef = React.createRef();
    this.pdfWrapperRef = React.createRef();
    this.buttonBack = this.buttonBack.bind(this);
    this.buttonNext = this.buttonNext.bind(this);
    this.buttonRotateRight = this.buttonRotateRight.bind(this);
    this.buttonRotateLeft = this.buttonRotateLeft.bind(this);
    this.renderPage = this.renderPage.bind(this);
    this.keyListener = this.keyListener.bind(this);
  }

  componentDidMount() {
    window.addEventListener("keyup", this.keyListener, true);
    this.loadPdf();
  }

  componentWillUnmount() {
    window.removeEventListener("keyup", this.keyListener, true);
  }

  componentDidUpdate(prevProps, prevState) {
    // Reload PDF if URL changes
    if (prevProps.state.url !== this.props.state.url) {
      this.loadPdf();
    }
    // Re-render page if page number, scale, or rotate changes
    if (
      prevState.pageNumber !== this.state.pageNumber ||
      prevState.scale !== this.state.scale ||
      prevState.rotate !== this.state.rotate
    ) {
      if (this.state.pdfDocument) {
        this.renderPage(this.state.pageNumber);
      }
    }
  }

  async loadPdf() {
    if (!pdflib || !this.props.state.url) {
      this.setState({ loading: false, error: "PDF URL not provided" });
      return;
    }

    try {
      this.setState({ loading: true, error: null });

      const isOfflineContext =
        typeof window !== "undefined" &&
        (window.location.protocol === "file:" || (typeof State !== "undefined" && State));

      let docUrl = this.props.state.url;

      let documentParams = docUrl;

      if (isOfflineContext && typeof docUrl === "string") {
        try {
          const scriptUrl = docUrl + ".js";
          const fileName = docUrl.split("/").pop();

          const loadScript = (src) => {
            return new Promise((resolve, reject) => {
              if (document.querySelector(`script[src="${src}"]`)) {
                resolve();
                return;
              }
              const script = document.createElement("script");
              script.src = src;
              script.onload = () => resolve();
              script.onerror = () => reject(new Error("Failed to load PDF script: " + src));
              document.head.appendChild(script);
            });
          };

          await loadScript(scriptUrl);

          if (window.pdfData && window.pdfData[fileName]) {
            const base64Data = window.pdfData[fileName];
            const binaryString = window.atob(base64Data);
            const len = binaryString.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            documentParams = {
              data: bytes, // Pass data directly
              disableRange: true,
              disableStream: true,
              disableAutoFetch: true,
            };
          } else {
            console.warn("PDF data not found in wrapper, falling back to direct URL.");

            documentParams = {
              url: docUrl,
              disableRange: true,
              disableStream: true,
              disableAutoFetch: true,
            };
          }
        } catch (err) {
          console.error("Error with PDF JS wrapper, trying direct load.", err);

          documentParams = {
            url: docUrl,
            disableRange: true,
            disableStream: true,
            disableAutoFetch: true,
          };
        }
      }

      const loadingTask = pdflib.getDocument({
        ...(typeof documentParams === "string" ? { url: documentParams } : documentParams),
        isEvalSupported: false, // pdfjs 3.x font-eval RCE (CVE-2024-4367)
      });
      const pdfDocument = await loadingTask.promise;

      let pageNumber = 1;
      if (this.props.state.currentState) {
        try {
          pageNumber = parseInt(this.props.state.currentState.split(",")[2], 10) || 1;
        } catch (e) {}
      }

      this.setState(
        {
          pdfDocument,
          numPages: pdfDocument.numPages,
          pageNumber,
          loading: false,
        },
        () => {
          this.renderPage(pageNumber);
        }
      );
    } catch (error) {
      console.error("Error loading PDF:", error);
      this.setState({
        loading: false,
        error: "Failed to load PDF: " + error.message,
      });
    }
  }

  async renderPage(pageNumber) {
    const { pdfDocument, scale, rotate } = this.state;
    if (!pdfDocument || !this.canvasRef.current) return;

    try {
      const page = await pdfDocument.getPage(pageNumber);
      const canvas = this.canvasRef.current;
      const context = canvas.getContext("2d");

      const viewport = page.getViewport({ scale, rotation: rotate });

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;
    } catch (error) {
      console.error("Error rendering page:", error);
    }
  }

  buttonNext() {
    if (this.state.pageNumber < this.state.numPages) {
      this.setState({
        pageNumber: this.state.pageNumber + 1,
      });
    }
  }

  buttonBack() {
    if (this.state.pageNumber > 1) {
      this.setState({
        pageNumber: this.state.pageNumber - 1,
      });
    }
  }

  buttonRotateRight() {
    this.setState({
      rotate: (this.state.rotate + 90) % 360,
    });
  }

  buttonRotateLeft() {
    this.setState({
      rotate: (this.state.rotate - 90 + 360) % 360,
    });
  }

  onClickFullscreen() {
    const wrapper = this.pdfWrapperRef.current;
    if (!this.state.fullscreen && wrapper) {
      screenfull.request(wrapper);
    } else {
      screenfull.exit();
    }
    this.setState({ fullscreen: !this.state.fullscreen });
  }

  keyListener(e) {
    if (this.state.fullscreen) {
      let key = e.keyCode ? e.keyCode : e.which;
      if (key === 34) {
        e.stopPropagation();
        this.buttonNext();
      } else if (key === 33) {
        e.stopPropagation();
        this.buttonBack();
      }
    }
  }

  onZoomIn() {
    this.setState({ scale: Math.min(5, this.state.scale + 0.25) });
  }

  onZoomOut() {
    this.setState({ scale: Math.max(0.25, this.state.scale - 0.25) });
  }

  render() {
    if (!pdfSupported) {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <p>PDF functionality is currently unavailable</p>
        </div>
      );
    }

    const { loading, error, numPages, pageNumber, rotate } = this.state;

    if (loading) {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <p>Please wait!</p>
        </div>
      );
    }

    if (error) {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <p>{error}</p>
        </div>
      );
    }

    let marks = this.props.props.marks || {};
    let markElements = Object.keys(marks).map((id) => {
      let value = marks[id].value;
      let title = marks[id].title;
      let color = marks[id].color;
      let position;
      if (value && value.split(",").length === 3) {
        position = value.split(",");
      } else {
        position = [0, 0, 0];
      }
      let x = "" + position[0] + "%";
      let y = "" + position[1] + "%";
      let bool = parseFloat(position[2]) === pageNumber;
      let isPopUp = marks[id].connectMode === "popup";
      let isVisor = true;
      return bool ? (
        <div
          key={id}
          style={{ position: "absolute", top: y, left: x, width: "24px", height: "26px" }}
        >
          <Mark
            color={color}
            idKey={id}
            title={title}
            isPopUp={isPopUp}
            isVisor={isVisor}
            markConnection={marks[id].connection}
            markValue={marks[id].value}
            boxID={this.props.props.id}
            onMarkClicked={(_id, _value) => {
              this.props.props.onMarkClicked(_id, _value, true);
            }}
          />
        </div>
      ) : null;
    });

    return (
      <div ref={this.pdfWrapperRef} style={{ width: "100%", height: "100%" }} className={"pdfDiv"}>
        <div className="topBar topBarVisor">
          <div className="rotationButtons">
            <button className={"PDFrotateL"} onClick={this.buttonRotateLeft}>
              <i className={"material-icons"}>rotate_left</i>
            </button>
            <button className={"PDFrotateR"} onClick={this.buttonRotateRight}>
              <i className={"material-icons"}>rotate_right</i>
            </button>
          </div>

          <div className="PDFpages">
            <button className={"PDFback"} onClick={this.buttonBack}>
              <i className={"material-icons"}>keyboard_arrow_left</i>
            </button>
            <span className={"PDFnumPages"}>
              {pageNumber} of {numPages || "?"}
            </span>
            <button className={"PDFnext"} onClick={this.buttonNext}>
              <i className={"material-icons"}>keyboard_arrow_right</i>
            </button>
          </div>
          <div className="actionButtons">
            <button
              className="fullscreen-player-button"
              onClick={this.onClickFullscreen.bind(this)}
            >
              {!this.state.fullscreen ? (
                <i className="material-icons">fullscreen</i>
              ) : (
                <i className="material-icons">fullscreen_exit</i>
              )}
            </button>
            <button
              className="scale-button"
              disabled={this.state.scale >= 5}
              onClick={this.onZoomIn.bind(this)}
            >
              {" "}
              <i className="material-icons">zoom_in</i>
            </button>
            <button
              className="scale-button"
              disabled={this.state.scale <= 0.25}
              onClick={this.onZoomOut.bind(this)}
            >
              {" "}
              <i className="material-icons">zoom_out</i>
            </button>
          </div>
        </div>

        <div className="dropableRichZone" style={{ position: "relative", overflow: "auto" }}>
          <canvas ref={this.canvasRef} className="pdfPage" />
          {rotate === 0 || rotate === 360 ? markElements : null}
        </div>
      </div>
    );
  }
}
/* eslint-enable react/prop-types */
